import { useEffect, useMemo, useRef, useState } from 'react'
import { IconButton, Stack, Typography } from '@mui/material_modern'
import { Close as CloseIcon } from '@mui/icons-material_modern'

import {
  ASSET_CATEGORY,
  DISPATCH_TICKET_ENUM,
  DISPATCH_TICKET_STATUSES,
  DISPATCH_TICKET_TYPES,
  DT_STATUS_ENUM,
} from 'utils/constants'
import SubFormLayout from 'components/layouts/SubFormLayout.jsx'
import { useFormContext } from 'react-hook-form'
import { useGetAssetsByCategoryQuery } from 'services/physicalAssets'
import { createTrailerOption, getSubHeadingSelectRow } from 'components/containers/load-board/utils/helperFunctions.jsx'

interface TransportInfoFormRowProps {
  ticketIsResolved: boolean
  customerId?: string | null
  dispatchTicketData: any
  driversData: any
  trucksData: any
}

const TransportInfoFormRow: React.FC<TransportInfoFormRowProps> = ({
  ticketIsResolved,
  customerId,
  dispatchTicketData,
  driversData,
  trucksData,
}) => {
  const { setValue, watch } = useFormContext()
  const [trailerSelectOptions, setTrailerSelectOptions] = useState<any[]>([])
  const [defaultTrailerSelected, setDefaultTrailerSelected] = useState(false)
  const [trailerIsDriverAssignedTrailer, setTrailerIsDriverAssignedTrailer] = useState(false)
  const shouldUpdateTruckTrailer = useRef(false)

  const selectedLocationId = watch('location.id')
  const selectedDriverId = watch('driverId')
  const selectedTruckId = watch('assetTruckId')
  const selectedTrailerId = watch('assetTrailerId')
  const ticketStatus = watch('status')
  const pickUpTrailer = watch('pickUpTrailer')
  const pickAndReturnTrailer = watch('pickAndReturnTrailer')
  const dispatchingYardLocationId = watch('dispatchingYardLocationId')
  const dispatchTicketType = watch('type')

  const isRequested = dispatchTicketData?.status === DT_STATUS_ENUM.REQUESTED

  // fetch trailers in yard
  const { data: trailersInInventoryData } = useGetAssetsByCategoryQuery(
    {
      assetCategory: ASSET_CATEGORY.TRAILER,
      filter: JSON.stringify({
        locationId: dispatchingYardLocationId,
      }),
    },
    { skip: !dispatchingYardLocationId },
  )
  // fetch trailers at location
  const { data: trailersDeployedData } = useGetAssetsByCategoryQuery(
    {
      assetCategory: ASSET_CATEGORY.TRAILER,
      filter: JSON.stringify({ locationId: selectedLocationId }),
    },
    { skip: !selectedLocationId },
  )
  const dispatchTicketTrailer = useMemo(() => {
    return dispatchTicketData?.dispatchTicketAssets?.find(assetStatus => {
      return assetStatus?.asset?.assetCategory === ASSET_CATEGORY.TRAILER
    })
  }, [dispatchTicketData])

  const dtTrailerIsAtCustomer = useMemo(() => {
    return dispatchTicketTrailer?.fromLocationId === selectedLocationId
  }, [selectedLocationId, dispatchTicketTrailer])

  // if a trailer is already on the DT this adds it to the list of trailers in inventory
  const trailersInInventory = useMemo(() => {
    let trailers = trailersInInventoryData?.Items || []
    if (dispatchTicketTrailer && !dtTrailerIsAtCustomer) {
      trailers = [dispatchTicketTrailer.asset, ...trailers]
    }
    return trailers
  }, [dispatchTicketTrailer, dtTrailerIsAtCustomer, trailersInInventoryData?.Items])

  // if a trailer is already on the DT this adds it to the list of trailers at location
  const trailersAtLocation = useMemo(() => {
    let trailers = trailersDeployedData?.Items || []
    if (dispatchTicketTrailer && dtTrailerIsAtCustomer) {
      trailers = [dispatchTicketTrailer.asset, ...trailers]
    }
    if (!selectedLocationId) return []
    return trailers
  }, [dispatchTicketTrailer, dtTrailerIsAtCustomer, trailersDeployedData?.Items, selectedLocationId])

  const onlyOneSelectOption = trailersAtLocation.length === 1 && (pickUpTrailer || pickAndReturnTrailer)

  // create trailer select options based on the dispatch ticket type
  useEffect(() => {
    //? create trailer select options for DROP_OFF type DT
    if (
      dispatchTicketType === DISPATCH_TICKET_ENUM.DROP_OFF ||
      dispatchTicketType === DISPATCH_TICKET_ENUM.EXCHANGE ||
      dispatchTicketType === DISPATCH_TICKET_ENUM.DELIVERY ||
      dispatchTicketType === DISPATCH_TICKET_ENUM.LIVE_LOAD
    ) {
      const inYardOpts = trailersInInventory?.map(trailer => createTrailerOption(trailer))
      setTrailerSelectOptions(inYardOpts)
    }

    //? create trailer select options for PICK_UP type DT
    if (
      dispatchTicketType === DISPATCH_TICKET_ENUM.PICK_UP ||
      dispatchTicketType === DISPATCH_TICKET_ENUM.PICK_AND_RETURN
    ) {
      // setup in yard options
      const inYardLabel = getSubHeadingSelectRow('In Yard')
      const inYardOptsWithLabel = trailersInInventory.length
        ? [inYardLabel, ...trailersInInventory.map(trailer => createTrailerOption(trailer))]
        : []
      // setup at location options
      const atLocationLabel = getSubHeadingSelectRow('At Location')
      const atLocationOptsWithLabel = trailersAtLocation.length
        ? [atLocationLabel, ...trailersAtLocation.map(trailer => createTrailerOption(trailer))]
        : []

      // if pickUpTrailer is selected, only show at location options
      if (pickUpTrailer || pickAndReturnTrailer) setTrailerSelectOptions(atLocationOptsWithLabel)
      else setTrailerSelectOptions([...inYardOptsWithLabel, ...atLocationOptsWithLabel])
    }
  }, [
    dispatchTicketTrailer,
    dispatchTicketType,
    dtTrailerIsAtCustomer,
    pickUpTrailer,
    pickAndReturnTrailer,
    trailersAtLocation,
    trailersDeployedData,
    trailersInInventoryData,
    trailersInInventory,
  ])

  // append driver on dispatch ticket to driver options
  const driverOptions = useMemo(() => {
    const drivers = [...(driversData?.Items || [])]
    if (dispatchTicketData?.driver?.id && !drivers.find(driver => driver.id === dispatchTicketData?.driver?.id)) {
      drivers.push(dispatchTicketData?.driver)
    }
    return drivers
  }, [dispatchTicketData, driversData])
  // append truck on dispatch ticket to truck options
  const truckOptions = useMemo(() => {
    const trucks = [...(trucksData?.Items || [])]
    const dispatchTicketTruck = dispatchTicketData?.dispatchTicketAssets?.find(
      dispatchTicketAsset => dispatchTicketAsset?.asset?.assetCategory === ASSET_CATEGORY.TRUCK,
    )

    if (dispatchTicketTruck && !trucks.find(truck => truck.id === dispatchTicketTruck?.asset?.id)) {
      trucks.push(dispatchTicketTruck.asset)
    }

    return trucks.map(truck => ({
      id: truck?.id,
      name: (
        <Stack>
          <Typography variant="body2">{truck?.assetName}</Typography>
          <Typography variant="caption">{truck?.type}</Typography>
        </Stack>
      ),
    }))
  }, [dispatchTicketData, trucksData])

  const shouldShowPickupDropoffCheckbox = useMemo(() => {
    return (
      dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_UP.id ||
      dispatchTicketType === DISPATCH_TICKET_TYPES.DROP_OFF.id ||
      dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id
    )
  }, [dispatchTicketType])

  // set up the correct checkbox form field based on the dispatch ticket type
  const trailerPickupDropOffSelectOpt = useMemo(() => {
    const selectedTrailerIsInYard = trailersInInventory.find(trailer => trailer.id === selectedTrailerId)

    if (dispatchTicketType === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
      return {
        name: 'dropOffTrailer',
        label: 'Drop Off Trailer',
        dataTest: 'drop-off-trailer',
        fieldType: 'checkbox',
        hidden: false,
        disabled: !trailersInInventory.length || ticketIsResolved || trailerIsDriverAssignedTrailer || isRequested,
        tooltipText: !trailersInInventory.length
          ? 'Available when trailer is in inventory'
          : trailerIsDriverAssignedTrailer
            ? 'This trailer is assigned to a driver and cannot be dropped off'
            : '',
        sx: { flex: 1 },
      }
    } else if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
      return {
        name: 'pickAndReturnTrailer',
        label: 'Pick & Return Trailer',
        dataTest: 'pick-and-return-trailer',
        fieldType: 'checkbox',
        disabled:
          !trailersAtLocation.length ||
          selectedTrailerIsInYard ||
          ticketIsResolved ||
          trailerIsDriverAssignedTrailer ||
          isRequested,
        tooltipText: !trailersAtLocation.length ? 'Available when trailer is deployed to a location' : '',
        sx: { flex: 1 },
      }
    } else if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_UP.id) {
      return {
        name: 'pickUpTrailer',
        label: 'Pick Up Trailer',
        dataTest: 'pick-up-trailer',
        fieldType: 'checkbox',
        disabled:
          !trailersAtLocation.length ||
          selectedTrailerIsInYard ||
          ticketIsResolved ||
          trailerIsDriverAssignedTrailer ||
          isRequested,
        tooltipText: !trailersAtLocation.length ? 'Available when trailer is deployed to a location' : '',
        sx: { flex: 1 },
      }
    }
  }, [dispatchTicketType, selectedTrailerId, ticketIsResolved, trailersAtLocation.length, trailersInInventory])

  // auto populate trailer selection when there is just one at yard
  useEffect(() => {
    const defaultPickupTrailerId = trailersAtLocation[0]?.id
    if ((pickUpTrailer || pickAndReturnTrailer) && trailersAtLocation.length === 1) {
      setValue('assetTrailerId', defaultPickupTrailerId)
      setDefaultTrailerSelected(true)
    }
    // when user un-checks the "Pickup Trailer"/ "pick and return" checkbox, clear the selection.
    else if (!dispatchTicketTrailer && !pickUpTrailer && !pickAndReturnTrailer && defaultTrailerSelected) {
      setValue('assetTrailerId', '')
      setDefaultTrailerSelected(false)
    }
  }, [defaultTrailerSelected, dispatchTicketTrailer, pickUpTrailer, pickAndReturnTrailer, setValue, trailersAtLocation])

  // handle updating checkbox based on user trailer selection clicks
  useEffect(() => {
    const defaultPickupTrailerId = trailersAtLocation[0]?.id
    const trailerIsAtLocation = !!trailersAtLocation.find(trailer => trailer.id === selectedTrailerId)
    if (selectedTrailerId && selectedTrailerId === defaultPickupTrailerId) {
      if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_UP.id) setValue('pickUpTrailer', true)
      if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) setValue('pickAndReturnTrailer', true)
      setDefaultTrailerSelected(true)
    } else if (trailerIsAtLocation) {
      if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_UP.id) setValue('pickUpTrailer', true)
      if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) setValue('pickAndReturnTrailer', true)
    }
  }, [dispatchTicketType, selectedTrailerId, setValue, trailersAtLocation, trailersInInventory])

  // if driver changes, update the truck to the driver's default truck
  useEffect(() => {
    // We do not want to do this on the initial render, or it will override the real values
    if (!shouldUpdateTruckTrailer.current) {
      return
    }

    const selectedDriver = driverOptions.find(driver => driver.id === selectedDriverId)

    if (selectedDriver?.truckId) {
      setValue('assetTruckId', selectedDriver.truckId, { shouldDirty: true })
    } else {
      setValue('assetTruckId', '', { shouldDirty: true })
    }

    if (selectedDriver?.trailerId) {
      setValue('assetTrailerId', selectedDriver.trailerId, { shouldDirty: true })
      // if selected driver has an assigned trailer, uncheck the pickup/drop off/pick and return checkbox and disable the checkboxes
      if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_UP.id) {
        setValue('pickUpTrailer', false, { shouldDirty: true })
      } else if (dispatchTicketType === DISPATCH_TICKET_TYPES.PICK_AND_RETURN.id) {
        setValue('pickAndReturnTrailer', false, { shouldDirty: true })
      } else if (dispatchTicketType === DISPATCH_TICKET_TYPES.DROP_OFF.id) {
        setValue('dropOffTrailer', false, { shouldDirty: true })
      }
      setTrailerIsDriverAssignedTrailer(true)
    } else {
      setValue('assetTrailerId', '', { shouldDirty: true })
      setTrailerIsDriverAssignedTrailer(false)
    }

    shouldUpdateTruckTrailer.current = false
  }, [driverOptions, selectedDriverId])

  const fields = [
    {
      name: 'driverId',
      label: 'Driver',
      dataTest: 'driver',
      selectProps: {
        onChange: ({ target: { value } }) => {
          shouldUpdateTruckTrailer.current = true
          setValue('driverId', value, { shouldDirty: true })
          if (ticketStatus === DISPATCH_TICKET_STATUSES.ASSIGNED.id && !value) {
            setValue('status', DISPATCH_TICKET_STATUSES.UNASSIGNED.id)
          } else if (ticketStatus === DISPATCH_TICKET_STATUSES.UNASSIGNED.id && value) {
            setValue('status', DISPATCH_TICKET_STATUSES.ASSIGNED.id)
          }
        },
        ...(!ticketIsResolved &&
          selectedDriverId && {
            IconComponent: () => null,
            endAdornment: (
              <IconButton
                onClick={() => {
                  setValue('driverId', '', { shouldDirty: true })
                  if (ticketStatus === DISPATCH_TICKET_STATUSES.ASSIGNED.id) {
                    setValue('status', DISPATCH_TICKET_STATUSES.UNASSIGNED.id)
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }),
      },
      fieldType: 'dropdown',
      defaultValue: null,
      dropdownOptions: driverOptions,
      disabled: !customerId || ticketIsResolved || isRequested,
      sx: { flex: 2 },
    },
    {
      name: 'assetTruckId',
      label: 'Truck',
      dataTest: 'truck',
      selectProps: {
        onChange: ({ target: { value } }) => {
          setValue('assetTruckId', value, { shouldDirty: true })
        },
        ...(!ticketIsResolved &&
          selectedTruckId && {
            IconComponent: () => null,
            endAdornment: (
              <IconButton
                onClick={() => {
                  setValue('assetTruckId', '', { shouldDirty: true })
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }),
      },
      fieldType: 'dropdown',
      defaultValue: null,
      dropdownOptions: truckOptions,
      disabled: !customerId || !selectedLocationId || ticketIsResolved || isRequested,
      sx: { flex: 2 },
    },
    {
      name: 'assetTrailerId',
      label: trailerSelectOptions.length ? 'Trailer' : 'NO TRAILERS AVAILABLE',
      dataTest: 'trailer',
      selectProps: {
        onChange: ({ target: { value } }) => {
          setValue('assetTrailerId', value, { shouldDirty: true })

          // check if trailer is assigned to any driver and disable the pickup/drop off/pick and return checkbox
          const associatedDriver = driverOptions.find(driver => driver.trailerId === value)
          if (associatedDriver) {
            setTrailerIsDriverAssignedTrailer(true)
          } else {
            setTrailerIsDriverAssignedTrailer(false)
          }
        },
        ...(!ticketIsResolved &&
          selectedTrailerId &&
          !onlyOneSelectOption && {
            IconComponent: () => null,
            endAdornment: (
              <IconButton
                onClick={() => {
                  setValue('assetTrailerId', '', { shouldDirty: true })
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }),
      },
      fieldType: 'dropdown',
      defaultValue: null,
      dropdownOptions: trailerSelectOptions,
      disabled: !customerId || !selectedLocationId || ticketIsResolved || !trailerSelectOptions.length || isRequested,
      sx: { flex: 2 },
    },
  ]

  if (shouldShowPickupDropoffCheckbox && trailerPickupDropOffSelectOpt) {
    fields.push(trailerPickupDropOffSelectOpt as any)
  }

  return <SubFormLayout formName="Driver Information" boxStyles={{ width: '100%' }} direction="row" fields={[fields]} />
}

export default TransportInfoFormRow
